import React, { useCallback, useState, memo, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, AlertCircle, Check, DollarSign, Shield, ArrowRight, Download, RefreshCw, Heart } from 'lucide-react';
import Papa from 'papaparse';
import { Header } from '../components/Header';
import { GlassCard } from '../components/ui/GlassCard';
import { DonateModal } from '../components/DonateModal';
// List of AI tools to detect
const AI_TOOLS = [{
  name: 'OpenAI / ChatGPT',
  patterns: ['openai', 'chatgpt', 'chat gpt']
}, {
  name: 'Anthropic / Claude',
  patterns: ['anthropic', 'claude']
}, {
  name: 'Midjourney',
  patterns: ['midjourney']
}, {
  name: 'GitHub Copilot',
  patterns: ['github', 'copilot']
}, {
  name: 'Replit',
  patterns: ['replit']
}, {
  name: 'Cursor',
  patterns: ['cursor']
}, {
  name: 'Perplexity',
  patterns: ['perplexity']
}, {
  name: 'Jasper',
  patterns: ['jasper']
}, {
  name: 'Copy.ai',
  patterns: ['copy.ai']
}, {
  name: 'Writesonic',
  patterns: ['writesonic']
}, {
  name: 'Grammarly',
  patterns: ['grammarly']
}, {
  name: 'Otter.ai',
  patterns: ['otter.ai', 'otter ai']
}, {
  name: 'Descript',
  patterns: ['descript']
}, {
  name: 'ElevenLabs',
  patterns: ['elevenlabs', 'eleven labs']
}, {
  name: 'Runway',
  patterns: ['runway']
}, {
  name: 'Stability AI',
  patterns: ['stability ai', 'stability.ai']
}, {
  name: 'Hugging Face',
  patterns: ['hugging face', 'huggingface']
}, {
  name: 'Notion AI',
  patterns: ['notion']
}, {
  name: 'Canva',
  patterns: ['canva']
}, {
  name: 'Adobe Firefly',
  patterns: ['adobe']
}, {
  name: 'Figma',
  patterns: ['figma']
}, {
  name: 'Framer',
  patterns: ['framer']
}, {
  name: 'Webflow',
  patterns: ['webflow']
}, {
  name: 'Zapier',
  patterns: ['zapier']
}, {
  name: 'Make',
  patterns: ['make.com', 'integromat']
}, {
  name: 'Synthesia',
  patterns: ['synthesia']
}, {
  name: 'Murf',
  patterns: ['murf']
}, {
  name: 'Pictory',
  patterns: ['pictory']
}, {
  name: 'Lumen5',
  patterns: ['lumen5']
}, {
  name: 'Character.AI',
  patterns: ['character.ai', 'character ai']
}, {
  name: 'Poe',
  patterns: ['poe.com', 'poe subscription']
}, {
  name: 'Quora',
  patterns: ['quora']
}, {
  name: 'Leonardo.ai',
  patterns: ['leonardo.ai', 'leonardo ai']
}, {
  name: 'Beautiful.ai',
  patterns: ['beautiful.ai']
}, {
  name: 'Gamma',
  patterns: ['gamma']
}, {
  name: 'Replicate',
  patterns: ['replicate']
}, {
  name: 'n8n',
  patterns: ['n8n']
}];
interface Subscription {
  tool: string;
  amount: number;
  date: string;
  description: string;
}
export function SubscriptionCheckerPage() {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'upload' | 'processing' | 'results'>('upload');
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
      processFile(droppedFile);
    } else {
      setError('Please upload a valid CSV file');
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      processFile(selectedFile);
    }
  };
  const processFile = (file: File) => {
    setStep('processing');
    setIsProcessing(true);
    setError(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        analyzeTransactions(results.data);
      },
      error: err => {
        setError('Error parsing CSV file: ' + err.message);
        setStep('upload');
        setIsProcessing(false);
      }
    });
  };
  const analyzeTransactions = (data: any[]) => {
    try {
      const foundSubscriptions: Subscription[] = [];
      let total = 0;
      // Helper to find column names flexibly
      const findColumn = (row: any, candidates: string[]) => {
        const keys = Object.keys(row).map(k => k.toLowerCase());
        for (const candidate of candidates) {
          const match = keys.find(k => k.includes(candidate));
          if (match) return Object.keys(row)[keys.indexOf(match)];
        }
        return null;
      };
      data.forEach(row => {
        // Find relevant columns
        const descCol = findColumn(row, ['description', 'merchant', 'payee', 'memo', 'name', 'details']);
        const amountCol = findColumn(row, ['amount', 'debit', 'value', 'cost']);
        const dateCol = findColumn(row, ['date', 'time', 'posted']);
        if (!descCol || !amountCol) return;
        const description = String(row[descCol]).toLowerCase();
        const rawAmount = String(row[amountCol]);
        // Parse amount - handle currency symbols and negative numbers
        let amount = parseFloat(rawAmount.replace(/[^0-9.-]+/g, ''));
        // If amount is negative (debit), make it positive for display
        if (amount < 0) amount = Math.abs(amount);
        // Skip if amount is NaN or 0
        if (isNaN(amount) || amount === 0) return;
        // Check against AI tool patterns
        for (const tool of AI_TOOLS) {
          if (tool.patterns.some(pattern => description.includes(pattern))) {
            foundSubscriptions.push({
              tool: tool.name,
              amount,
              date: dateCol ? String(row[dateCol]) : 'Unknown date',
              description: String(row[descCol])
            });
            total += amount;
            break; // Stop checking other tools if match found
          }
        }
      });
      // Sort by amount descending
      foundSubscriptions.sort((a, b) => b.amount - a.amount);
      setSubscriptions(foundSubscriptions);
      setTotalAmount(total);
      // Artificial delay for better UX
      setTimeout(() => {
        setStep('results');
        setIsProcessing(false);
      }, 1500);
    } catch (err) {
      console.error(err);
      setError('Error analyzing transactions. Please check your CSV format.');
      setStep('upload');
      setIsProcessing(false);
    }
  };
  const reset = () => {
    setFile(null);
    setSubscriptions([]);
    setTotalAmount(0);
    setStep('upload');
    setError(null);
  };
  const exportResults = () => {
    const csv = Papa.unparse(subscriptions);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'ai_subscriptions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Helper function to get tool website for unsubscribe link
  const getToolWebsite = (toolName: string): string => {
    const websites: {
      [key: string]: string;
    } = {
      'OpenAI / ChatGPT': 'https://platform.openai.com/account/billing',
      'Anthropic / Claude': 'https://console.anthropic.com/settings/billing',
      Midjourney: 'https://www.midjourney.com/account',
      'GitHub Copilot': 'https://github.com/settings/copilot',
      Replit: 'https://replit.com/account',
      Cursor: 'https://cursor.sh/settings',
      Perplexity: 'https://www.perplexity.ai/settings',
      Jasper: 'https://app.jasper.ai/settings/billing',
      'Copy.ai': 'https://app.copy.ai/settings/billing',
      Writesonic: 'https://app.writesonic.com/settings',
      Grammarly: 'https://account.grammarly.com/subscription',
      'Otter.ai': 'https://otter.ai/account',
      Descript: 'https://www.descript.com/account',
      ElevenLabs: 'https://elevenlabs.io/subscription',
      Runway: 'https://app.runwayml.com/account',
      'Stability AI': 'https://platform.stability.ai/account',
      'Hugging Face': 'https://huggingface.co/settings/billing',
      'Notion AI': 'https://www.notion.so/my-account',
      Canva: 'https://www.canva.com/settings/billing',
      'Adobe Firefly': 'https://account.adobe.com',
      Figma: 'https://www.figma.com/settings',
      Framer: 'https://www.framer.com/settings/billing',
      Webflow: 'https://webflow.com/dashboard/account/billing',
      Zapier: 'https://zapier.com/app/settings/billing',
      Make: 'https://www.make.com/en/user/subscription',
      Synthesia: 'https://app.synthesia.io/account',
      Murf: 'https://murf.ai/account',
      Pictory: 'https://app.pictory.ai/settings',
      Lumen5: 'https://lumen5.com/settings',
      'Character.AI': 'https://beta.character.ai/settings',
      Poe: 'https://poe.com/settings',
      Quora: 'https://www.quora.com/settings',
      'Leonardo.ai': 'https://app.leonardo.ai/settings',
      'Beautiful.ai': 'https://www.beautiful.ai/account',
      Gamma: 'https://gamma.app/settings',
      Replicate: 'https://replicate.com/account',
      n8n: 'https://n8n.io/pricing'
    };
    return websites[toolName] || 'https://www.google.com/search?q=' + encodeURIComponent(toolName + ' unsubscribe');
  };
  return <div className="min-h-screen bg-black text-white flex flex-col" data-id="element-1134">
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-1135">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-full blur-[120px]" data-id="element-1136" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/5 to-pink-500/5 rounded-full blur-[120px]" data-id="element-1137" />
      </div>

      <Header data-id="element-1138" />

      <main className="relative z-10 flex-1 pt-[156px] pb-16 px-4" data-id="element-1139">
        <div className="max-w-4xl mx-auto" data-id="element-1140">
          <div className="text-center mb-12" data-id="element-1141">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} style={{
            marginBottom: '40px'
          }} className="font-bold font-special [font-size:1.875rem] md:[font-size:3.75rem] [line-height:2.4rem] md:[line-height:4.2rem]" data-id="element-1142">
              AI Subscription Checker
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-1143">
              Upload your credit card statement CSV to instantly find all your
              AI tool subscriptions and see how much you're spending.
            </motion.p>
          </div>

          <AnimatePresence mode="wait" data-id="element-1144">
            {step === 'upload' && <motion.div key="upload" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} data-id="element-1145">
                <GlassCard className="p-8 md:p-12 border-dashed border-2 border-white/10 hover:border-cyan-500/30 transition-colors" data-id="element-1146">
                  <div className={`flex flex-col items-center justify-center min-h-[300px] rounded-xl transition-all ${isDragging ? 'bg-white/5 scale-[1.02]' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-1147">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-6" data-id="element-1148">
                      <Upload size={32} className="text-cyan-400" data-id="element-1149" />
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2 font-special text-center" data-id="element-1150">
                      Drag & Drop your CSV file here
                    </h3>
                    <p className="text-gray-400 mb-8" data-id="element-1151">
                      or click to browse from your computer
                    </p>

                    <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" id="csv-upload" data-id="element-1152" />
                    <label htmlFor="csv-upload" className="px-8 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium cursor-pointer transition-all border border-white/10 hover:border-white/30" data-id="element-1153">
                      Select CSV File
                    </label>

                    {error && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="mt-6 flex items-center gap-2 text-red-400 bg-red-500/10 px-4 py-2 rounded-lg" data-id="element-1154">
                        <AlertCircle size={18} data-id="element-1155" />
                        <span data-id="element-1156">{error}</span>
                      </motion.div>}

                    <div className="mt-12 flex items-center gap-2 text-gray-500 text-sm" data-id="element-1157">
                      <Shield size={16} data-id="element-1158" />
                      <span data-id="element-1159">
                        Your data is processed locally in your browser and never
                        uploaded.
                      </span>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>}

            {step === 'processing' && <motion.div key="processing" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="flex flex-col items-center justify-center min-h-[400px]" data-id="element-1160">
                <div className="relative w-24 h-24 mb-8" data-id="element-1161">
                  <div className="absolute inset-0 rounded-full border-4 border-white/10" data-id="element-1162"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-cyan-500 animate-spin" data-id="element-1163"></div>
                  <FileText className="absolute inset-0 m-auto text-white/50" size={32} data-id="element-1164" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1165">
                  Analyzing Transactions...
                </h3>
                <p className="text-gray-400" data-id="element-1166">
                  Scanning for AI tool subscriptions
                </p>
              </motion.div>}

            {step === 'results' && <motion.div key="results" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="space-y-6" data-id="element-1167">
                {/* Summary Card */}
                <GlassCard className="p-8 bg-gradient-to-br from-cyan-900/10 to-purple-900/10" data-id="element-1168">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1169">
                    <div data-id="element-1170">
                      <h2 className="text-lg text-gray-400 mb-1" data-id="element-1171">
                        Total Monthly AI Spend
                      </h2>
                      <div className="text-5xl font-bold text-white font-special" data-id="element-1172">
                        ${totalAmount.toFixed(2)}
                      </div>
                      <p className="text-sm text-gray-500 mt-2" data-id="element-1173">
                        Across {subscriptions.length} active subscriptions
                      </p>
                    </div>

                    <div className="flex gap-3" data-id="element-1174">
                      <button onClick={exportResults} disabled={subscriptions.length === 0} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1175">
                        <Download size={16} data-id="element-1176" />
                        Export CSV
                      </button>
                      <button onClick={reset} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-sm font-medium" data-id="element-1177">
                        <RefreshCw size={16} data-id="element-1178" />
                        Check Another
                      </button>
                    </div>
                  </div>
                </GlassCard>

                {/* Subscriptions List */}
                <div className="grid gap-4" data-id="element-1179">
                  {subscriptions.length > 0 ? subscriptions.map((sub, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.05
              }} data-id="element-1180">
                        <GlassCard className="p-6 hover:bg-white/5 transition-all group" data-id="element-1181">
                          <div className="flex items-center justify-between gap-6" data-id="element-1182">
                            <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-1183">
                              {/* Tool Logo */}
                              <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:border-white/20 transition-colors flex-shrink-0" data-id="element-1184">
                                <span className="text-2xl font-bold bg-gradient-to-br from-cyan-400 to-purple-400 bg-clip-text text-transparent" data-id="element-1185">
                                  {sub.tool.charAt(0)}
                                </span>
                              </div>

                              {/* Tool Info */}
                              <div className="flex-1 min-w-0" data-id="element-1186">
                                <h3 className="font-bold text-white text-lg mb-1" data-id="element-1187">
                                  {sub.tool}
                                </h3>
                                <p className="text-sm text-gray-500 truncate" data-id="element-1188">
                                  {sub.description}
                                </p>
                                <p className="text-xs text-gray-600 mt-1" data-id="element-1189">
                                  {sub.date}
                                </p>
                              </div>
                            </div>

                            {/* Amount and Action */}
                            <div className="flex items-center gap-4 flex-shrink-0" data-id="element-1190">
                              <div className="text-right" data-id="element-1191">
                                <div className="text-2xl font-bold text-white" data-id="element-1192">
                                  ${sub.amount.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-1193">
                                  per month
                                </div>
                              </div>

                              <a href={getToolWebsite(sub.tool)} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/30 text-sm font-medium text-gray-400 hover:text-cyan-400 transition-all whitespace-nowrap" data-id="element-1194">
                                Unsubscribe →
                              </a>
                            </div>
                          </div>
                        </GlassCard>
                      </motion.div>) : <GlassCard className="p-12 text-center" data-id="element-1195">
                      <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-1196">
                        <Check size={32} className="text-green-500" data-id="element-1197" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-1198">
                        No AI Subscriptions Found
                      </h3>
                      <p className="text-gray-400" data-id="element-1199">
                        We couldn't detect any known AI tool subscriptions in
                        this CSV file.
                      </p>
                      <button onClick={reset} className="mt-6 text-cyan-400 hover:text-cyan-300 font-medium" data-id="element-1200">
                        Try another file
                      </button>
                    </GlassCard>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-6 bg-black/80 backdrop-blur-lg mt-auto" data-id="element-1201">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-1202">
          <div className="flex items-center gap-2" data-id="element-1203">
            <span style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 300,
            fontSize: '1.125rem',
            lineHeight: '1.75rem',
            letterSpacing: '.15em'
          }} className="text-white" data-id="element-1204">
              FAIND
            </span>
          </div>
          <div className="text-gray-500 text-sm" data-id="element-1205">
            © {new Date().getFullYear()} Faind. All rights reserved.
          </div>
          <div className="flex items-center gap-6" data-id="element-1206">
            <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Follow us on X" data-id="element-1207">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" data-id="element-1208">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-1209" />
              </svg>
            </a>
            <button onClick={() => setIsDonateModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400 transition-all text-sm font-medium" data-id="element-1210">
              <Heart size={14} data-id="element-1211" />
              Donate
            </button>
          </div>
        </div>
      </footer>

      <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data-id="element-1212" />
    </div>;
}